import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Star, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { companyInfo } from '../mock';
import { getServicesData } from '../data/servicesData';
import { trackPhoneClick, trackEmailClick } from './GoogleAnalytics';

const ZONE_LINKS = [
  { service: 'beton-imprime', label: 'Béton Imprimé', cities: [
    { slug: 'orange', name: 'Orange' },
    { slug: 'avignon', name: 'Avignon' },
    { slug: 'carpentras', name: 'Carpentras' },
    { slug: 'nimes', name: 'Nîmes' },
    { slug: 'cavaillon', name: 'Cavaillon' },
    { slug: 'montelimar', name: 'Montélimar' },
  ]},
  { service: 'beton-desactive', label: 'Béton Désactivé', cities: [
    { slug: 'orange', name: 'Orange' },
    { slug: 'avignon', name: 'Avignon' },
    { slug: 'arles', name: 'Arles' },
    { slug: 'nimes', name: 'Nîmes' },
    { slug: 'tarascon', name: 'Tarascon' },
    { slug: 'chateaurenard', name: 'Châteaurenard' },
  ]},
  { service: 'beton-bale', label: 'Béton Balayé', cities: [
    { slug: 'orange', name: 'Orange' },
    { slug: 'avignon', name: 'Avignon' },
    { slug: 'bollene', name: 'Bollène' },
    { slug: 'sorgues', name: 'Sorgues' },
    { slug: 'uzes', name: 'Uzès' },
    { slug: 'pierrelatte', name: 'Pierrelatte' },
  ]},
  { service: 'beton-lisse', label: 'Béton Lissé', cities: [
    { slug: 'orange', name: 'Orange' },
    { slug: 'avignon', name: 'Avignon' },
    { slug: 'carpentras', name: 'Carpentras' },
    { slug: 'bagnols-sur-ceze', name: 'Bagnols-sur-Cèze' },
    { slug: 'vaison-la-romaine', name: 'Vaison-la-Romaine' },
    { slug: 'apt', name: 'Apt' },
  ]},
  { service: 'beton-cire', label: 'Béton Ciré', cities: [
    { slug: 'orange', name: 'Orange' },
    { slug: 'avignon', name: 'Avignon' },
    { slug: 'isle-sur-la-sorgue', name: "L'Isle-sur-la-Sorgue" },
    { slug: 'vaison-la-romaine', name: 'Vaison-la-Romaine' },
    { slug: 'saint-remy-de-provence', name: 'St-Rémy-de-Provence' },
    { slug: 'nyons', name: 'Nyons' },
  ]},
];

const ADMIN_ROUTES = ['/admin'];

const Footer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const services = getServicesData(i18n.language);
  const [zonesOpen, setZonesOpen] = useState(false);

  if (ADMIN_ROUTES.some(r => location.pathname.startsWith(r))) return null;

  return (
    <footer className="bg-slate-950 text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="site-footer">
      <div className="container mx-auto">
        {/* Main footer grid */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-bold mb-4">{companyInfo.name}</h3>
            <p className="text-slate-400 mb-4">{companyInfo.slogan}</p>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-4">{t('nav.services')}</h4>
            <ul className="space-y-2 text-slate-400">
              {services.slice(0, 5).map((service) => (
                <li key={service.id}>
                  <Link to="/services" className="hover:text-amber-400 transition-colors">{service.title}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/" className="hover:text-amber-400 transition-colors">{t('nav.home')}</Link></li>
              <li><Link to="/services" className="hover:text-amber-400 transition-colors">{t('nav.services')}</Link></li>
              <li><Link to="/galerie" className="hover:text-amber-400 transition-colors">Galerie</Link></li>
              <li><Link to="/about" className="hover:text-amber-400 transition-colors">{t('nav.about')}</Link></li>
              <li><Link to="/comment-ca-marche" className="hover:text-amber-400 transition-colors">{t('nav.howItWorks')}</Link></li>
              <li><Link to="/faq" className="hover:text-amber-400 transition-colors">{t('nav.faq')}</Link></li>
              <li><Link to="/contact" className="hover:text-amber-400 transition-colors">{t('nav.contact')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">{t('nav.contact')}</h4>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-start gap-2">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href={`tel:${companyInfo.phoneRaw}`} onClick={trackPhoneClick} className="hover:text-amber-400 transition-colors">{companyInfo.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} onClick={trackEmailClick} className="hover:text-amber-400 transition-colors">{companyInfo.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Zones d'intervention - Desktop: always visible, Mobile: collapsible */}
        <div className="border-t border-slate-800 pt-8 mb-8" data-testid="footer-zones">
          <button
            className="md:hidden w-full flex items-center justify-between mb-4"
            onClick={() => setZonesOpen(!zonesOpen)}
            data-testid="zones-toggle-btn"
          >
            <h4 className="font-bold text-sm uppercase tracking-wider text-slate-300">
              Nos zones d'intervention
            </h4>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${zonesOpen ? 'rotate-180' : ''}`} />
          </button>
          <h4 className="hidden md:block font-bold text-sm uppercase tracking-wider text-slate-300 mb-5">
            Nos zones d'intervention
          </h4>

          <div className={`${zonesOpen ? 'block' : 'hidden'} md:block`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-4">
              {ZONE_LINKS.map(group => (
                <div key={group.service}>
                  <p className="text-xs font-semibold text-amber-400/80 uppercase tracking-wider mb-2">{group.label}</p>
                  <ul className="space-y-1">
                    {group.cities.map(city => (
                      <li key={city.slug}>
                        <Link
                          to={`/${group.service}-${city.slug}`}
                          className="text-slate-500 hover:text-amber-400 text-xs transition-colors"
                          data-testid={`zone-link-${group.service}-${city.slug}`}
                        >
                          {city.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Link
                to="/zones-intervention"
                className="text-xs text-amber-400/70 hover:text-amber-400 transition-colors inline-flex items-center gap-1"
                data-testid="zones-see-all-link"
              >
                Voir toutes nos zones d'intervention &rarr;
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
          <p>&copy; {new Date().getFullYear()} {companyInfo.name} - SIRET: {companyInfo.siret}</p>
          <p className="mt-2">
            {t('footer.allRightsReserved')} &bull; {t('footer.expertSince')} {companyInfo.established}
          </p>
          <p className="mt-2">
            <Link to="/confidentialite" className="hover:text-amber-400 transition-colors underline">
              {t('rdv.privacyPolicy')}
            </Link>
            {' \u2022 '}
            <span>{t('footer.dataProtection')}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
